import React, { useState, useEffect, Fragment } from "react";

import { KeyboardArrowLeft } from "@material-ui/icons";
import PropTypes from "prop-types";

import { consumerSendVerificationCode } from "../../../../lib/api/auth";
import { ErrorMessage } from "../ErrorMessage";
import { LoadingSpinner } from "../../../../components/LoadingSpinner";
import "./style.scss";
import { CustomButton } from "../../../../components/ui-kit/CustomButtom";
import { formattingPhoneNumber, unformattedPhoneNumber } from "lib/helpers/formatters";

import { useTranslation } from 'react-i18next';
import {
  wtError
} from "lib/helpers/dataLayers";

export const AuthFormVerification = ({
  handleSetCurrentStep,
  setUserEmail,
  setUserPhone,
  userPhone,
  userEmail,
  handleGoBack,
  isButtonDisabled,
  setIsButtonDisabled,
  initial_login
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [verificationForm, setVerificationForm] = useState({});
  const [isEmailChecked, setIsEmailChecked] = useState(
    !window.matchMedia(
      "only screen and (max-width: 760px) and (orientation: portrait)"
    ).matches
  );
  const [isVerificationDisabled, setIsVerificationDisabled] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    if (validateEmail(userEmail)) {
      setVerificationForm({ email: userEmail });
      setIsVerificationDisabled(false);
    }
  }, [userEmail]);

  const handleCheck = (e) => {
    if (e.target.id === "email") {
      setIsEmailChecked(true);
      if (validateEmail(userEmail)) {
        setIsVerificationDisabled(false);
      } else {
        setIsVerificationDisabled(true);
      }
    } else {
      setIsEmailChecked(false);
      if (!isNaN(userPhone) && unformattedPhoneNumber(userPhone).length === 10) {
        setIsVerificationDisabled(false);
        setVerificationForm({ phone_number: unformattedPhoneNumber(userPhone) });
      } else {
        setIsVerificationDisabled(true);
      }
    }
  };

  const handleVerificationChange = (e) => {
    const lowercasedEmail = e.target.value.toLowerCase();
    if (isEmailChecked) {
      if (validateEmail(lowercasedEmail)) {
        setIsVerificationDisabled(false);
        setVerificationForm({
          [e.target.name]: lowercasedEmail,
        });
      } else {
        setIsVerificationDisabled(true);
      }
      setUserEmail(lowercasedEmail);
    } else if (!isEmailChecked) {
      setUserPhone(e.target.value);
      setVerificationForm({
        [e.target.name]: `${unformattedPhoneNumber(e.target.value)}`,
      });
      if (formattingPhoneNumber(e.target.value).length === 14 ) {
        setIsVerificationDisabled(false);
      } else {
        setIsVerificationDisabled(true);
      }
    }
  };

  const handleVerification = async () => {
    setIsButtonDisabled(true);
    setLoading(true);
    const data = await consumerSendVerificationCode({ ...verificationForm, login_flow: true });

    if (data.success === "True") {
      if (isEmailChecked) {
        setUserPhone("");
      } else {
        setUserEmail("");
      }
      setErrorMessage("");
      setIsButtonDisabled(false);
      handleSetCurrentStep("3");
    } else {
      wtError();
      setIsButtonDisabled(false);
      setErrorMessage(data.message);
    }
    setLoading(false);
  };

  const validateEmail = (email) => {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
    return re.test(String(email).toLowerCase());
  };

  return (
    <div className="auth-form-verification">
      <div className="auth-form__header">
        <h2 className="heading heading-1">{t("Enable Two-Factor Authentication")}</h2>
        <p className="heading heading-body-medium">
          {t("Where do you want to receive your verification code?")}
        </p>
        <button
          className="button button-text--simple"
          onClick={() => handleGoBack()}
        >
          <KeyboardArrowLeft />
        </button>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Fragment>
          {!initial_login && (
            <>
              <div className="form-group form-group--verification">
                <div className="d-flex">
                  <label className="verification-radio" htmlFor="phone">
                    <input
                      type="radio"
                      id="phone"
                      name="verificationRadio"
                      defaultChecked={!isEmailChecked}
                      onClick={(e) => handleCheck(e)}
                    />
                    <span className="label">{t("Receive via text")}</span>
                    <span className="checkmark"></span>
                  </label>
                </div>
              </div>
              <div className="input-wrapper">
                <input
                  onChange={handleVerificationChange}
                  className="form-control"
                  type="text"
                  name="phone_number"
                  placeholder="XXX-XXX-XXXX"
                  disabled={isEmailChecked}
                  value={formattingPhoneNumber(userPhone)}
                  id="phone"
                />
                <span className="phone-prefix"></span>
              </div>
            </>
          )}
          <div className="form-group form-group--verification marg-top-30">
            <div className="d-flex">
              <label className="verification-radio" htmlFor="email">
                <input
                  type="radio"
                  id="email"
                  name="verificationRadio"
                  defaultChecked={isEmailChecked}
                  onClick={(e) => handleCheck(e)}
                />
                <span className="label">{t("Receive via email")}</span>
                <span className="checkmark"></span>
              </label>
            </div>
          </div>
          <div className="input-wrapper">
            <input
              onChange={handleVerificationChange}
              className="form-control"
              type="text"
              name="email"
              placeholder="example@email.com"
              disabled={!isEmailChecked && !validateEmail(userEmail)}
              value={userEmail}
              id="email"
            />
          </div>
          {errorMessage ? (
            <ErrorMessage
              errorMessage={errorMessage}
              className="error-message verification"
            />
          ) : null}
          <div className="auth-form__submit">
            <CustomButton
              text={t("Send code")}
              size="medium"
              buttonId="sendcode2FA"
              onClickHandler={handleVerification}
              disabled={isVerificationDisabled || isButtonDisabled}
            />
          </div>
        </Fragment>
      )}
    </div>
  );
};

AuthFormVerification.propTypes = {
  handleSetCurrentStep: PropTypes.func,
  setUserEmail: PropTypes.func,
  setUserPhone: PropTypes.func,
  userPhone: PropTypes.string,
  userEmail: PropTypes.string,
  handleGoBack: PropTypes.func,
  isButtonDisabled: PropTypes.bool,
  setIsButtonDisabled: PropTypes.func,
};
